/**
 * What a board arrow AGGREGATES: every call between two ends, grouped by the
 * interface it goes through.
 *
 * One home, because two callers need the identical walk and the subtle part of
 * it — the `nested` rule — is easy to get subtly wrong in a second copy. The
 * inspector reads it to describe a connection a reader clicked; a guided story
 * reads it to narrate a beat. Both are asking the same question of the graph,
 * so they must not be able to answer it differently.
 */
import { isCallEdge, type DependencyGraph } from "@archlang/engine";

/** Is this call's endpoint at `base`, or anywhere inside it? The `.` guard stops
 *  `Order` from swallowing a sibling called `Orders`. */
export const inTree = (path: string, base: string): boolean =>
  path === base || path.startsWith(`${base}.`);

/** The interface an edge lands on, spelled the way an inspector reads it —
 *  bare inside the callee, qualified when it belongs to someone else. */
export function interfaceOf(edge: { readonly toInterfacePath: readonly string[] }): string {
  const path = edge.toInterfacePath;
  return path[path.length - 1] ?? "";
}

export interface CallAggregate {
  /** Interface name → how many calls go through it, and in which processes.
   *  Insertion-ordered by first appearance, so a caller can quote "the first
   *  few interfaces" and mean the ones the graph presents first. */
  readonly byInterface: Map<string, { count: number; processes: Set<string> }>;
  /** Process id → display name, for callers that link to a process. */
  readonly processes: Map<string, string>;
}

/**
 * Every call from one subtree to another, grouped by interface.
 *
 * SUBTREE, not exact pair. Both addresses name endpoints at the level the board
 * drew them: an arrow joins the two endpoints' ancestors AT THE SHARED SPACE,
 * and a port's peer is that same ancestor. So on any nested workspace neither
 * end need be the module that actually makes the call — `ibs → aws` is a real
 * arrow the reader can click, while every call behind it runs to something
 * INSIDE `aws`. Matching exact pairs answered null for all of them, and the
 * panel said "not in the current model" about an arrow drawn from that model.
 *
 * The `nested` guard is for the case where one end CONTAINS the other — a port
 * on a descendant peering back at its own ancestor space, which is how a
 * parent↔child call is drawn. There the two subtrees overlap, so a call with
 * both endpoints inside the DEEPER of them never crosses the boundary being
 * described and must not be counted. It has to be the deeper one specifically:
 * excluding by the from-side would throw away the legitimate parent → child
 * call, whose callee is inside the parent by definition.
 */
export function callsBetweenPaths(
  graph: DependencyGraph | null,
  fromPath: string,
  toPath: string,
): CallAggregate {
  // The deeper subtree, when one end sits inside the other; null when disjoint,
  // which is the ordinary sibling case.
  const nested = inTree(toPath, fromPath)
    ? toPath
    : inTree(fromPath, toPath)
      ? fromPath
      : null;
  const byInterface = new Map<string, { count: number; processes: Set<string> }>();
  const processes = new Map<string, string>();
  for (const edge of graph?.edges ?? []) {
    if (!isCallEdge(edge)) continue;
    const edgeFrom = edge.fromModulePath.join(".");
    const edgeTo = edge.toModulePath.join(".");
    if (!inTree(edgeFrom, fromPath) || !inTree(edgeTo, toPath)) continue;
    if (nested && inTree(edgeFrom, nested) && inTree(edgeTo, nested)) continue;
    const name = interfaceOf(edge);
    const seen = byInterface.get(name) ?? { count: 0, processes: new Set<string>() };
    seen.count += 1;
    if (edge.processName) seen.processes.add(edge.processName);
    if (edge.processId) processes.set(edge.processId, edge.processName ?? edge.processId);
    byInterface.set(name, seen);
  }
  return { byInterface, processes };
}
